/**
 * Navigation abstraction layer.
 *
 * Funnels all route changes through a single `navigate()` function,
 * decoupling screens from the routing engine.
 *
 * Uses React Router with browser-history URLs.
 *
 * Usage from React (ES module):
 *   import { navigate, navigateToCallCard } from '@/services/navigation';
 *   navigate({ section: 'CAD', tab: 'InProgress', fn: 'CAD_InProgress' });
 *   navigateToCallCard(123);
 *
 * Usage from legacy code (window global):
 *   window.OrnaReact.navigate.to({ section: 'CAD', tab: 'InProgress' });
 *   window.OrnaReact.navigate.toCallCard(123);
 */

import { getNavigateRef } from '@/router/navigate-ref';
import { buildHashPath, legacyUrlToHashPath } from '@/router/route-config';

// ── Core types ────────────────────────────────────────────────────────

export interface NavigateOptions {
  /** Module section: CAD, MDT, ADMIN, PAYMENT, Home */
  section: string;
  /** Tab name (e.g. InProgress, Search, CallCard) */
  tab?: string;
  /** Screen registry key (e.g. CAD_InProgress, Open_Person) */
  fn?: string;
  /** Whether this is a multi-module page (used for admin route URL building) */
  multiModule?: boolean;
  /** Route params string (pipe or comma separated) */
  params?: string;
  /** Extra state passed via React Router's navigation state (not in the URL) */
  state?: Record<string, any>;
}

// ── Helpers ───────────────────────────────────────────────────────────

function getModuleTypeUpper(): string {
  const moduleType = (window as any).getModuleType?.() || '';
  return moduleType.toUpperCase();
}

/**
 * Use React Router's navigate function. Falls back to full navigation if ref not set yet.
 */
function routerNavigate(hashPath: string, extraState?: Record<string, any>): void {
  const nav = getNavigateRef();
  if (nav) {
    // Pass unique state to force React Router to re-trigger even for same-path navigation
    nav(hashPath, { state: { t: Date.now(), ...extraState } });
  } else {
    // Fallback: full page navigation (router will pick it up on load)
    window.location.pathname = hashPath;
  }
}

// ── Core navigate ─────────────────────────────────────────────────────

/**
 * Navigate to a page. When React Router is active, converts to a hash path.
 * When page.js is active, builds a query-string URL and calls page.show().
 */
export function navigate(opts: NavigateOptions): void {
  const hashPath = buildHashPath(opts);
  routerNavigate(hashPath, opts.state);
}

/**
 * Navigate using a raw URL string. For edge cases and backwards compatibility.
 */
export function navigateRaw(url: string): void {
  const hashPath = legacyUrlToHashPath(url);
  if (hashPath) {
    routerNavigate(hashPath);
  }
}

/**
 * Refresh the current page by re-running the current route.
 * Uses navigate(0) which is React Router's equivalent of history.go(0).
 */
export function refreshPage(): void {
  const nav = getNavigateRef();
  if (nav) {
    nav(0);
  }
}

/**
 * Refresh with updated params appended to the current URL.
 */
export function refreshWithParams(params: string): void {
  const currentPath = window.location.pathname;
  routerNavigate(currentPath + '/' + params);
}

// ── Convenience: Home / Module Selection ──────────────────────────────

export function navigateToHome(): void {
  navigate({ section: 'Home' });
}

export function navigateToModuleHome(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({ section: s, tab: 'Home', fn: `${s}_Home` });
}

export function navigateToModuleSelected(section: string): void {
  navigate({ section, tab: 'Home', fn: `${section}_Selected` });
}

export function navigateToSignOut(): void {
  routerNavigate('/signout');
}

export function navigateToPayment(): void {
  navigate({
    section: 'PAYMENT',
    tab: 'PaymentManagement',
    fn: 'PAYMENT_PaymentManagement',
    multiModule: true,
  });
}

// ── Convenience: In Progress / Officer Status ─────────────────────────

export function navigateToInProgress(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({ section: s, tab: 'InProgress', fn: `${s}_InProgress` });
}

export function navigateToOfficerStatus(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({ section: s, tab: 'OfficerStatus', fn: `${s}_OfficerStatus` });
}

// ── Convenience: Call Cards ───────────────────────────────────────────

export function navigateToNewCallCard(): void {
  navigate({
    section: 'CAD',
    tab: 'NewCallCard',
    fn: 'CAD_CallCard',
    multiModule: true,
  });
}

export function navigateToCallCard(callCardId: number | string, section?: string): void {
  const s = section || getModuleTypeUpper();
  if (s === 'CAD') {
    navigate({
      section: 'CAD',
      tab: 'NewCallCard',
      fn: 'CAD_CallCard',
      multiModule: true,
      params: String(callCardId),
    });
  } else {
    navigate({
      section: 'MDT',
      tab: 'SelfInitiate',
      fn: 'MDT_ShowCallCard',
      multiModule: true,
      params: String(callCardId),
    });
  }
}

/** Get the URL string for a CAD call card (for href attributes, not navigation). */
export function getCallCardUrl(callCardId: number | string): string {
  return `/cad/call-card/${callCardId}`;
}

/**
 * Navigate to an existing CAD call card (used by GlobalEvents, InProgress, etc.)
 * Legacy alias: getExistingCadCallCardRelativeUrl().
 */
export function getExistingCadCallCardUrl(callCardId: number | string): string {
  return `/cad/call-card/${callCardId}`;
}

// ── Convenience: Tour Stop Completions ─────────────────────────────────

export function navigateToTourStopCompletion(completionId: number | string, section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({
    section: s,
    tab: 'Search',
    fn: 'TourStopCompletion',
    multiModule: true,
    params: String(completionId),
  });
}

export function getTourStopCompletionUrl(completionId: number | string, section?: string): string {
  const s = (section || 'cad').toLowerCase();
  return `/${s}/tour-stop-completion/${completionId}`;
}

// ── Convenience: Case Reports ─────────────────────────────────────────

export function navigateToCaseReport(
  reportId: number | string,
  isLog?: boolean,
  viewOnly?: boolean,
  section?: string,
  currentTab?: string
): void {
  const s = section || getModuleTypeUpper();
  const state: Record<string, any> = {};
  if (isLog !== undefined) state.isLog = isLog;
  if (viewOnly !== undefined) state.viewOnly = viewOnly;
  if (currentTab) state.currentTab = currentTab;
  navigate({
    section: s,
    tab: 'CaseReports',
    fn: 'displayCaseReport',
    multiModule: true,
    params: String(reportId),
    state,
  });
}

export function navigateToNewCaseReport(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({
    section: s,
    tab: 'CaseReports',
    fn: 'displayCaseReport',
    multiModule: true,
  });
}

export function navigateToCaseReportList(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({ section: s, tab: 'CaseReports', fn: `${s}_CaseReports`, multiModule: true });
}

/** Get URL for a case report (for href attributes). */
export function getCaseReportUrl(
  reportId: number | string,
  section?: string
): string {
  const s = section || getModuleTypeUpper();
  return `/${s.toLowerCase()}/case-report/${reportId}`;
}

// ── Convenience: Citations ────────────────────────────────────────────

export function navigateToNewCitation(personId?: number, section?: string): void {
  const s = section || getModuleTypeUpper();
  const params = personId ? `new,${personId}` : 'new';
  navigate({
    section: s,
    tab: 'Citations',
    fn: 'Open_CitationForm',
    multiModule: true,
    params,
  });
}

export function navigateToCitation(
  citationId: number | string,
  mode: 'edit' | 'view' = 'view',
  section?: string
): void {
  const s = section || getModuleTypeUpper();
  navigate({
    section: s,
    tab: 'Citations',
    fn: 'Open_CitationForm',
    multiModule: true,
    params: `${mode},,,${citationId}`,
  });
}

// ── Convenience: NPSD (Records) ───────────────────────────────────────

export function navigateToPerson(personId: number | string, section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({
    section: s,
    tab: 'Search',
    fn: 'Open_Person',
    multiModule: true,
    params: String(personId),
  });
}

export function navigateToNewPerson(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({
    section: s,
    tab: 'Search',
    fn: 'Open_Person',
    multiModule: true,
  });
}

export function navigateToVehicle(vehicleId: number | string, section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({
    section: s,
    tab: 'Search',
    fn: 'Open_Vehicle',
    multiModule: true,
    params: String(vehicleId),
  });
}

export function navigateToNewVehicle(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({
    section: s,
    tab: 'Search',
    fn: 'Open_Vehicle',
    multiModule: true,
  });
}

export function navigateToBusiness(businessId: number | string, section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({
    section: s,
    tab: 'Search',
    fn: 'Open_Business',
    multiModule: true,
    params: String(businessId),
  });
}

export function navigateToNewBusiness(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({
    section: s,
    tab: 'Search',
    fn: 'Open_Business',
    multiModule: true,
  });
}

export function navigateToClientGroup(clientGroupId: number | string, section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({
    section: s,
    tab: 'Search',
    fn: 'Open_ClientGroup',
    multiModule: true,
    params: String(clientGroupId),
  });
}

export function navigateToAdminClientGroup(clientGroupId?: number | string): void {
  const opts: NavigateOptions = {
    section: 'ADMIN',
    tab: 'ClientGroupManagement',
    fn: 'Open_AdminClientGroup',
    multiModule: true,
  };
  if (clientGroupId != null) opts.params = String(clientGroupId);
  navigate(opts);
}

/**
 * Navigate to a record by type (person, vehicle, business, client).
 * Used by connection panels that handle multiple record types.
 */
export function navigateToRecord(
  type: 'person' | 'vehicle' | 'business' | 'client',
  id: number | string,
  section?: string
): void {
  switch (type) {
    case 'person':
      navigateToPerson(id, section);
      break;
    case 'vehicle':
      navigateToVehicle(id, section);
      break;
    case 'business':
      navigateToBusiness(id, section);
      break;
    case 'client':
      navigateToClientGroup(id, section);
      break;
  }
}

// ── Convenience: Search ───────────────────────────────────────────────

export function navigateToSearch(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({ section: s, tab: 'Search', fn: `${s}_Search`, multiModule: true });
}

export function navigateToSearchResult(
  type: string,
  id: number | string,
  section?: string
): void {
  const s = section || getModuleTypeUpper();
  const fnMap: Record<string, string> = {
    person: 'Open_Person',
    vehicle: 'Open_Vehicle',
    business: 'Open_Business',
    client: 'Open_ClientGroup',
    callcard: s === 'CAD' ? 'CAD_CallCard' : 'MDT_ShowCallCard',
  };
  const screenFn = fnMap[type];
  if (screenFn) {
    navigate({
      section: s,
      tab: 'Search',
      fn: screenFn,
      multiModule: true,
      params: String(id),
    });
  }
}

// ── Convenience: Map ──────────────────────────────────────────────────

export function navigateToMap(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({ section: s, tab: 'Map', fn: `${s}_Map`, multiModule: true });
}

export function navigateToMapByAddress(address: string, section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({
    section: s,
    tab: 'Map',
    fn: 'NPSD_MapByAddress',
    multiModule: true,
    params: encodeURIComponent(address),
  });
}

export function navigateToMapByCallCardId(callCardId: number | string, section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({
    section: s,
    tab: 'Map',
    fn: `${s}_MapById`,
    multiModule: true,
    params: String(callCardId),
  });
}

// ── Convenience: Common tabs ──────────────────────────────────────────

export function navigateToLog(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({ section: s, tab: 'Log', fn: `${s}_Log`, multiModule: true });
}

export function navigateToNotes(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({ section: s, tab: 'Notes', fn: `${s}_Notes`, multiModule: true });
}

// Operational advisory screen — single adaptive surface shown on both CAD and MDT.
// Optional prefilter pre-scopes the board to a client/site (used for CallCard /
// client / site deep-links to "advisories for this site"). The filter is handed
// off via a window global so both React and legacy callers can trigger it; the
// AdvisoryScreen consumes and clears it on mount.
export function navigateToAdvisories(
  section?: string,
  prefilter?: { clientId: number | null; siteId?: number | null }
): void {
  const s = section || getModuleTypeUpper();
  if (prefilter && prefilter.clientId != null) {
    (window as any).__ornaAdvisoryPrefilter = {
      clientId: prefilter.clientId,
      siteId: prefilter.siteId != null ? prefilter.siteId : null,
    };
  }
  navigate({ section: s, tab: 'Advisories', fn: `${s}_Advisories`, multiModule: true });
}

// Back-compat alias — the old read-only officer board is merged into the
// operational advisory screen above.
export function navigateToAdvisoryBoard(section?: string): void {
  navigateToAdvisories(section);
}

export function navigateToMessaging(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({ section: s, tab: 'Messaging', fn: `${s}_Messaging`, multiModule: true });
}

export function navigateToUserProfile(section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({ section: s, tab: 'UserProfile', fn: `${s}_UserProfile`, multiModule: true });
}

export function navigateToUserProfileWithPerson(personId: number | string, section?: string): void {
  const s = section || getModuleTypeUpper();
  navigate({
    section: s,
    tab: 'UserProfile',
    fn: `${s}_UserProfile`,
    multiModule: true,
    params: String(personId),
  });
}

// ── Public API object for window.OrnaReact.navigate ───────────────────

export const navigationApi = {
  to: navigate,
  raw: navigateRaw,
  refresh: refreshPage,
  refreshWithParams,

  toHome: navigateToHome,
  toModuleHome: navigateToModuleHome,
  toModuleSelected: navigateToModuleSelected,
  toSignOut: navigateToSignOut,
  toPayment: navigateToPayment,
  toInProgress: navigateToInProgress,
  toOfficerStatus: navigateToOfficerStatus,

  toNewCallCard: navigateToNewCallCard,
  toCallCard: navigateToCallCard,
  getCallCardUrl,
  getExistingCadCallCardUrl,

  toCaseReport: navigateToCaseReport,
  toNewCaseReport: navigateToNewCaseReport,
  toCaseReportList: navigateToCaseReportList,
  getCaseReportUrl,

  toNewCitation: navigateToNewCitation,
  toCitation: navigateToCitation,

  toPerson: navigateToPerson,
  toNewPerson: navigateToNewPerson,
  toVehicle: navigateToVehicle,
  toNewVehicle: navigateToNewVehicle,
  toBusiness: navigateToBusiness,
  toNewBusiness: navigateToNewBusiness,
  toClientGroup: navigateToClientGroup,
  toAdminClientGroup: navigateToAdminClientGroup,
  toRecord: navigateToRecord,

  toSearch: navigateToSearch,
  toSearchResult: navigateToSearchResult,

  toMap: navigateToMap,
  toMapByAddress: navigateToMapByAddress,
  toMapByCallCardId: navigateToMapByCallCardId,

  toLog: navigateToLog,
  toNotes: navigateToNotes,
  toAdvisories: navigateToAdvisories,
  toAdvisoryBoard: navigateToAdvisoryBoard,
  toMessaging: navigateToMessaging,
  toUserProfile: navigateToUserProfile,
  toUserProfileWithPerson: navigateToUserProfileWithPerson,
};
